// MarketPlaceModal.jsx
import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Spinner, Row, Col, InputGroup } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";

export default function MarketPlaceModal({ show, onHide, productId, onSuccess }) {
    const [marketPlaces, setMarketPlaces] = useState([]);
    const [selectedMarketPlace, setSelectedMarketPlace] = useState("");
    const [selectedBaseUrl, setSelectedBaseUrl] = useState("");
    const [type, setType] = useState("link");
    const [reference, setReference] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show) {
            setLoading(true);
            axios
                .post(
                    `${process.env.REACT_APP_API_URL}/market-placer/initial-data`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("authToken")}`
                        }
                    }
                )
                .then((res) => {
                    if (res.data?.success) {
                        setMarketPlaces(res.data.market_places || []);
                    }
                })
                .catch((err) => console.error(err))
                .finally(() => setLoading(false));
        } else {
            resetForm();
        }
    }, [show]);

    const resetForm = () => {
        setSelectedMarketPlace("");
        setSelectedBaseUrl("");
        setType("link");
        setReference("");
    };

    const handleMarketPlaceChange = (e) => {
        const id = e.target.value;
        setSelectedMarketPlace(id);
        const found = marketPlaces.find((mp) => String(mp.id) === String(id));
        setSelectedBaseUrl(found?.base_url || "");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedMarketPlace) return;

        setSubmitting(true);
        axios
            .post(
                `${process.env.REACT_APP_API_URL}/market-placer/products-market-places`,
                {
                    product_id: productId,
                    market_place_id: selectedMarketPlace,
                    type: type,
                    reference: reference
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            )
            .then((res) => {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: res.data?.message || "Marketplace added successfully!",
                    timer: 2000,
                    showConfirmButton: false
                });
                resetForm();
                onHide();
                if (onSuccess) onSuccess();
            })
            .catch((err) => {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Something went wrong"
                });
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton className="px-4 pt-4 border-0">
                <Modal.Title className="fw-bold">Add Marketplace</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 pt-2">
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-3 align-items-end">
                            {/* Marketplace Selection */}
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted">
                                        Marketplace
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedMarketPlace}
                                        onChange={handleMarketPlaceChange}
                                        required
                                    >
                                        <option value="">Select Marketplace</option>
                                        {marketPlaces.map((mp) => (
                                            <option key={mp.id} value={mp.id}>
                                                {mp.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Type Selection */}
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted">
                                        Type
                                    </Form.Label>
                                    <Form.Select
                                        value={type}
                                        onChange={(e) => {
                                            setType(e.target.value);
                                            setReference("");
                                        }}
                                        required
                                    >
                                        <option value="link">Link</option>
                                        <option value="id">Reference ID</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Combined BaseURL + ID or Direct Reference Link */}
                            {type === "id" ? (
                                <Col md={7}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold small text-uppercase text-muted">
                                            Reference (Base URL + ID)
                                        </Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text className="bg-light text-muted">
                                                {selectedBaseUrl || "Base URL"}
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                value={reference}
                                                onChange={(e) => setReference(e.target.value)}
                                                placeholder="e.g. 12345"
                                                required
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            ) : (
                                <Col md={7}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold small text-uppercase text-muted">
                                            Reference Link
                                        </Form.Label>
                                        <Form.Control
                                            type="url"
                                            value={reference}
                                            onChange={(e) => setReference(e.target.value)}
                                            placeholder="https://example.com/product/123"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        <div className="d-flex justify-content-end gap-2 mt-4 pt-2">
                            <Button variant="secondary" className="px-4" onClick={onHide}>
                                CANCEL
                            </Button>
                            <Button variant="primary" className="px-4" type="submit" disabled={submitting}>
                                {submitting ? "SAVING..." : "SAVE"}
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
}